import React from "react";

import Modal from "./Modal";
import SquareButton from "../Buttons/SquareButton";
import Text from "../Text/Text";
import { TrackingObject } from "../../types/TrackingTypes";
import styles from "./EmailCaptureSuccessModal.module.scss";

interface EmailCaptureSuccessModalProps {
    buttonText?: string;
    buttonUrl?: string;
    headerText: string;
    imgAltText?: string;
    imgUrl: string;
    isOpen: boolean;
    onClose?: (event?: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    onSuccessButtonClicked?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | undefined) => void;
    setIsOpen: (x: boolean) => void;
    successText: string;
    impressionTrackingProps?: TrackingObject;
    closeTrackingProps?: TrackingObject;
    successRef?: (node: HTMLDivElement) => void;
}

/**
 * Generate an email capture success modal attached as a portal. This modal does not accept children.
 * @param {Function} props.closeButtonClassName - An optional CSS module to override the close button styles.
 * @param {Function} props.onClose - An optional function to call when the close button is clicked.
 * @param {Function} props.onSuccessButtonClicked - An optional function to call when the success button is clicked.
 * @param {string} props.buttonUrl - the url to redirect to when the success button is clicked.
 * @param {string} props.buttonText - Text for within the submit button.
 * @param {string} props.headerText - Text to display in the header.
 * @param {string} props.imgAltText - The alt text for the image.
 * @param {string} props.imgUrl - A url to an image for the header.
 * @param {string} props.successText - A blob of text to display on the success screen before the CTA.
 * @param {Boolean} props.isOpen - Is the modal currently open?
 * @param {Function} props.setIsOpen - A function to set the state of the modal from outside.
 * @param {Function} props.successRef - An optional callback ref.
 * @example <Modal {...args} />
 * @returns An ADA compliant modal as a [React Portal](https://reactjs.org/docs/portals.html).
 */
const EmailCaptureSuccessModal = (props: EmailCaptureSuccessModalProps) => {
    const {
        buttonText,
        buttonUrl,
        headerText,
        imgAltText,
        imgUrl,
        isOpen,
        onClose,
        onSuccessButtonClicked = () => { },
        setIsOpen,
        successText,
        impressionTrackingProps,
        closeTrackingProps,
        successRef
    } = props;

    return (
        <Modal
            {...{closeTrackingProps, impressionTrackingProps}}
            closeButtonClassName={styles.successCloseButton}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            containerClassName={styles.successModal}
            contentClassName={styles.successContent}
            onClose={onClose}
            label="Successful sign up"
        >
            <div
                className={styles.successContainer}
                data-tracking-props={JSON.stringify(impressionTrackingProps)}
                data-tracking-event-type="impression"
                ref={successRef}
            >
                <div className={styles.imageContainer}>
                    <img src={imgUrl} alt={imgAltText} />
                    <Text className={styles.imageContainerText} textStyle="sans-serif-body">{headerText}</Text>
                </div>
                <div className={styles.textContainer}>
                    <div className={styles.successText}>{successText}</div>
                    {buttonText && buttonUrl && (
                        <SquareButton
                            href={buttonUrl}
                            target="_blank"
                            color="royal-blue"
                            onClick={onSuccessButtonClicked}
                            className={styles.successCTA}
                        >
                            {buttonText}
                        </SquareButton>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export { EmailCaptureSuccessModal as default };
export type { EmailCaptureSuccessModalProps };
